import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useField } from "formik";
import { AutoMenu } from "../../auto-menu";
import { DropdownIcon } from "../../dropdown-icon";
import { Input } from "../../input";
import { IProps } from "./select-with-groups.types";
import { Icon } from "../../icon";

const SelectWithGroups = <Item,>({
    name,
    getLabel,
    getValue,
    getActive,
    inputProps,
    items,
    searchPlaceholder = "Search...",
    selectPlaceholder = "Select item",
    noItemsFoundLabel = "No items found",
    onSelect,
    ...autoMenuWithPlainItemsProps
}: IProps<Item>) => {
    const [field, , helpers] = useField(name);

    const [opened, setOpened] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedValue = field.value;

    const selectedItem = useMemo(
        () => items?.find((item) => getValue(item) === selectedValue),
        [items, selectedValue, getValue]
    );

    const selectedItemGroupIcon = useMemo(() => {
        if (!selectedItem || !autoMenuWithPlainItemsProps.getGroupIcon || !autoMenuWithPlainItemsProps.getGroup) {
            return undefined;
        }
        const group = autoMenuWithPlainItemsProps.getGroup(selectedItem);
        if (!group) return undefined;
        return autoMenuWithPlainItemsProps.getGroupIcon(group);
    }, [selectedItem, autoMenuWithPlainItemsProps]);

    const displayValue = useMemo(() => (selectedItem ? getLabel(selectedItem) : ""), [selectedItem, getLabel]);

    const filteredItems = useMemo(() => {
        if (!items) return [];

        return items
            .map((item) => ({
                ...item,
                active: getValue(item) === selectedValue,
            }))
            .filter((item) => {
                if (!searchValue.trim()) return true;
                const label = getLabel(item);
                return label.toLowerCase().includes(searchValue.toLowerCase());
            });
    }, [items, selectedValue, searchValue, getValue, getLabel]);

    // Focus input when menu opens
    useEffect(() => {
        if (opened && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
        }
    }, [opened]);

    const handleSelect = useCallback(
        (value: string | number) => {
            helpers.setValue(value);
            setOpened(false);
            setSearchValue("");
            setIsSearching(false);

            if (onSelect) {
                onSelect(value.toString());
            }
        },
        [helpers, onSelect]
    );

    const handleInputClick = useCallback(() => {
        setOpened((prev) => !prev);
        if (!opened) {
            setIsSearching(true);
        } else {
            setSearchValue("");
            setIsSearching(false);
            inputRef.current?.blur();
        }
    }, [opened]);

    const handleInputKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if ((event.key === "Enter" || event.key === "ArrowDown") && !opened) {
                event.preventDefault();
                setOpened(true);
                setIsSearching(true);
            } else if (event.key === "Escape" && opened) {
                event.preventDefault();
                setOpened(false);
                setSearchValue("");
                setIsSearching(false);
                inputRef.current?.blur();
            }
        },
        [opened]
    );

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setSearchValue(value);
            setIsSearching(true);

            if (!opened) {
                setOpened(true);
            }
        },
        [opened]
    );

    const handleClear = useCallback(() => {
        helpers.setValue("");
        setSearchValue("");
        setIsSearching(false);
        if (opened) {
            setOpened(false);
        }
        if (onSelect) {
            onSelect("");
        }
    }, [helpers, onSelect, opened]);

    const handleMenuClose = useCallback((isOpened: boolean) => {
        setOpened(isOpened);
        if (!isOpened) {
            setSearchValue("");
            setIsSearching(false);
        }
    }, []);

    const inputValue = useMemo(
        () => (isSearching ? searchValue : displayValue),
        [isSearching, searchValue, displayValue]
    );

    const inputPlaceholder = useMemo(
        () => (isSearching ? searchPlaceholder : displayValue || selectPlaceholder),
        [isSearching, displayValue, searchPlaceholder, selectPlaceholder]
    );

    const isClearable = useMemo(() => !!selectedValue, [selectedValue]);

    return (
        <AutoMenu.WithPlainItems
            items={filteredItems}
            getLabel={getLabel}
            getValue={getValue}
            getActive={getActive || ((item) => item.active)}
            onClick={handleSelect}
            opened={opened}
            onChange={handleMenuClose}
            closeOnItemClick={true}
            moveActiveItemToTop
            hasCustomSearch
            {...autoMenuWithPlainItemsProps}
        >
            <Input
                ref={inputRef}
                placeholder={inputPlaceholder}
                value={inputValue}
                leftSection={selectedItemGroupIcon ? <Icon size="sm" icon={selectedItemGroupIcon} /> : undefined}
                rightSection={<DropdownIcon isOpen={opened} />}
                onClear={handleClear}
                onClick={handleInputClick}
                onKeyDown={handleInputKeyDown}
                onChange={handleInputChange}
                pointer
                aria-expanded={opened}
                aria-haspopup="menu"
                isClearable={isClearable}
                data-testid="select-with-groups-input"
                name={name}
                autoComplete="off"
                {...inputProps}
            />
        </AutoMenu.WithPlainItems>
    );
};

export { SelectWithGroups };
